/**
 * check_overlaps: walk a deck's pages (or take the scene as it stands), read
 * every part at rest with trace-viewer's fit geometry, and word the faults
 * fit-findings.ts finds, one category at a time.
 */
import { Matrix } from '@babylonjs/core/Maths/math.vector';
import { BehaviorGraph, Bevel, BoxCollider, Pages, Text } from 'trace-viewer/behaviors';
import { partsIn, walkDeck } from 'trace-viewer/build';
import { measureScene } from 'trace-viewer/quality';
import type { SceneObject } from 'trace-viewer/scene';
import { resolveUniqueByName, type ToolContext } from './agent-helpers';
import { textUnderButtons, type TextUnderButton } from './board-check';
import { findFit, type FitFindings, type Floating, type Move, type Placed } from './fit-findings';
import { TOOL_RESULT_MAX_CHARS } from './tool-image';

const DEFAULT_MAX = 10;

type Rgb = { r: number; g: number; b: number };
/** A material's linear colour as the sRGB hex a build gave it. */
const hex = (c: Rgb): string => `#${[c.r, c.g, c.b].map(v => Math.round(Math.min(1, Math.max(0, v)) ** (1 / 2.2) * 255).toString(16).padStart(2, '0')).join('').toUpperCase()}`;

/** A part's colours as drawn: its own meshes', or its pieces' for a group of bare primitives. */
function coloursOf(part: SceneObject): string {
    const own = part.getOwnedMeshes();
    const meshes = own.length ? own : part.getChildren().flatMap(c => c.getOwnedMeshes());
    const colours = new Set(meshes.map(m => {
        const material = m.material as { albedoColor?: Rgb; diffuseColor?: Rgb } | null;
        const c = material?.albedoColor ?? material?.diffuseColor;
        return c ? hex(c) : '';
    }).filter(Boolean));
    return [...colours].sort().join('+');
}

/** As a name shows it: one colour, or a figure's few ("#1B1B1B/#F4F4F4"). */
const colourLabel = (colours: string): string => {
    const list = colours.split('+').filter(Boolean);
    return list.length ? ` ${list.slice(0, 3).join('/')}${list.length > 3 ? '/…' : ''}` : '';
};

/** Its shape, whatever its pose: how many solids, and their surface area to 3 figures. */
function shapeOf(placed: Placed): string {
    let area = 0;
    for (const solid of placed.pose.part.solids) for (let i = 4; i < solid.faces.length; i += 5) area += solid.faces[i];
    return `${placed.pose.part.solids.length}:${area.toPrecision(3)}`;
}

/** A name without its copy number ("Castor 2", "Bolt #3", "Tile_4"). */
const baseName = (name: string): string => name.replace(/(?:\s+#?|#|_|\.)\d+$/, '') || name;

/** What names a group of parts: the words their names share, else the first two names. */
function groupName(names: readonly string[]): string {
    const distinct = [...new Set(names.map(baseName))];
    if (distinct.length === 1) return distinct[0];
    const words = distinct.map(n => n.split(/\s+/));
    const shared: string[] = [];
    for (let i = 0; words.every(w => w[i] !== undefined && w[i] === words[0][i]); i++) shared.push(words[0][i]);
    return shared.length ? shared.join(' ') : `${distinct[0]} / ${distinct[1]}${distinct.length > 2 ? ' …' : ''}`;
}

/**
 * The step a shown caption names: label(text, {step}) builds a group of "<name> Badge" (a pill), "<name> Number"
 * (the step) and "<name> Text". The first such group with a step number that is not "!" (a caution) wins.
 */
function captionStep(deck: SceneObject, model: SceneObject): string | null {
    for (const o of deck.getDescendants()) {
        if (!o.effectiveActive || ancestors(o).includes(model)) continue;
        const kids = o.getChildren(), child = (suffix: string) => kids.find(c => c.name === `${o.name}${suffix}`);
        const number = child(' Number')?.getBehavior(Text)?.getData().text?.trim();
        if (kids.length === 3 && child(' Badge')?.getBehavior(Bevel)?.getData().pill && child(' Text')?.getBehavior(Text) && number && /\d/.test(number)) return number;
    }
    return null;
}

/**
 * The parts each page shows first (cues aside), grouped by shape and colour: "page 13 (step 12-2): +2 Arch 1x4
 * #1B1B1B, +1 Tile 1x2 #F4F4F4"; "(to page 9)" when the deck hides them again before its last page. Pages that add
 * none are listed together.
 */
function newPartsText(pages: readonly ReadonlyMap<SceneObject, Placed>[], lastPage: ReadonlyMap<SceneObject, number>, steps: readonly (string | null)[], only?: number): string[] {
    const seen = new Set<SceneObject>();
    const lines: string[] = [], none: string[] = [];
    const label = (k: number): string => `${k + 1}${steps[k] ? ` (step ${steps[k]})` : ''}`;
    pages.forEach((page, k) => {
        const groups = new Map<string, { parts: SceneObject[]; colour: string; to: number }>();
        for (const [part, placed] of page) {
            if (seen.has(part)) continue;
            seen.add(part);
            if (!lastPage.has(part) || (only !== undefined && k !== only)) continue;
            const colour = coloursOf(part), key = `${colour}|${shapeOf(placed)}`, to = lastPage.get(part)!;
            const group = groups.get(key);
            if (group) { group.parts.push(part); group.to = Math.max(group.to, to); } else groups.set(key, { parts: [part], colour, to });
        }
        if (only !== undefined && k !== only) return;
        if (!groups.size) { none.push(label(k)); return; }
        lines.push(`page ${label(k)}: ${[...groups.values()].map(g => `+${g.parts.length} ${groupName(g.parts.map(p => p.name))}${colourLabel(g.colour)}${g.to < pages.length - 1 ? ` (to page ${g.to + 1})` : ''}`).join(', ')}`);
    });
    if (none.length) lines.push(`No new parts: page${none.length === 1 ? '' : 's'} ${none.join(', ')}.`);
    return lines;
}

/**
 * The deck's model: the child the most pages set states in, counted once per page, so a nav board whose slots
 * every page sets ties with the model; the child with more meshes wins a tie.
 */
function modelOf(deck: SceneObject): SceneObject {
    const model = modelIn(deck);
    if (!model) throw new Error(`"${deck.name}" has Pages but no page state targets one of its children: pass the model to check as objects.`);
    return model;
}

/** {@link modelOf}, or undefined when no page state targets a child. */
function modelIn(deck: SceneObject): SceneObject | undefined {
    const children = deck.getChildren();
    const pageCounts = new Map<SceneObject, number>();
    for (const page of deck.getBehavior(Pages)!.getData().pages ?? []) {
        const targeted = new Set((page.states ?? []).map(assigned => {
            const first = String(assigned.target ?? '').replace(/^\.\//, '').split('/')[0];
            return children.find(c => c.name === first);
        }));
        for (const child of targeted) if (child) pageCounts.set(child, (pageCounts.get(child) ?? 0) + 1);
    }
    const meshes = (o: SceneObject): number => o.getDescendants().reduce((n, d) => n + d.getOwnedMeshes().length, o.getOwnedMeshes().length);
    return [...pageCounts].sort((a, b) => b[1] - a[1] || meshes(b[0]) - meshes(a[0]))[0]?.[0];
}

const ancestors = (o: SceneObject): SceneObject[] => { const out: SceneObject[] = []; for (let n: SceneObject | null = o; n; n = n.getParent()) out.push(n); return out; };

function placedIn(object: SceneObject, frame: SceneObject, pose: Placed['pose']): Placed {
    const parent = object.getParent();
    parent?.transform.computeWorldMatrix(true);
    const toParent = frame.transform.getWorldMatrix().multiply(parent ? Matrix.Invert(parent.transform.getWorldMatrix()) : Matrix.Identity()).m;
    const p = object.transform.position;
    return { pose, toParent: [toParent[0], toParent[1], toParent[2], toParent[4], toParent[5], toParent[6], toParent[8], toParent[9], toParent[10]], position: [p.x, p.y, p.z] };
}

export async function checkOverlaps(input: Record<string, unknown>, ctx: ToolContext): Promise<string> {
    const { objects, page, max = DEFAULT_MAX } = input;
    if (objects !== undefined && (!Array.isArray(objects) || !objects.length || objects.length > 128 || objects.some(v => typeof v !== 'string' || !v.trim()))) {
        throw new Error('objects must list 1-128 object names or paths; omit it to check the scene\'s deck.');
    }
    if (page !== undefined && (typeof page !== 'number' || !Number.isInteger(page) || page < 1)) throw new Error('page must be a page number, 1 for the first.');
    if (typeof max !== 'number' || !Number.isInteger(max) || max < 1 || max > 30) throw new Error('max must be 1-30.');
    const scene = ctx.traceScene;
    let roots: SceneObject[];
    if (objects) roots = (objects as string[]).map(ref => resolveUniqueByName(scene, ref, 'check_overlaps'));
    else {
        const decks = scene.getSceneObjects().filter(o => o.getBehavior(Pages));
        if (decks.length !== 1) throw new Error(decks.length ? `the scene has ${decks.length} decks: pass the model to check as objects.` : 'the scene has no deck: pass the objects to check.');
        roots = decks;
    }
    roots = [...new Set(roots.map(r => (r.getBehavior(Pages) ? modelOf(r) : r)))];
    const frame = roots.length === 1 ? roots[0] : ancestors(roots[0]).find(a => roots.every(r => ancestors(r).includes(a))) ?? roots[0];
    const deck = ancestors(frame).find(a => a.getBehavior(Pages)) ?? null;
    const count = deck?.getBehavior(Pages)!.getPageCount() ?? 1;
    if (page !== undefined && !deck) throw new Error('page needs a deck (an object with Pages) around the objects.');
    if (page !== undefined && page > count) throw new Error(`page ${page}: "${deck!.name}" has ${count} pages.`);

    const snapshot = (): Map<SceneObject, Placed> => {
        const out = new Map<SceneObject, Placed>();
        for (const [object, pose] of partsIn(frame, roots)) out.set(object, placedIn(object, frame, pose));
        return out;
    };
    const steps: (string | null)[] = [];
    const covered = new Map<string, TextUnderButton & { pages: number[] }>();
    // The deck's whole model is not board content, whatever part of it `objects` names; a deck whose pages name no
    // model (checked by parts) has no board to judge.
    const deckModel = deck ? modelIn(deck) : undefined;
    /** Board text under a Nav (or choice) button on this page: an object outside the model wired to act when selected. */
    const board = (k: number): void => {
        if (!deckModel) return;
        const buttons = deck!.getDescendants().filter(o => o.effectiveActive && !ancestors(o).includes(deckModel) && pressable(o)).map(o => o.getPath());
        if (!buttons.length) return;
        const model = deckModel.getPath(), top = `${deck!.getPath()}/`, inDeck = (p: string): string => p.slice(top.length);
        // Board content: the deck's own text, outside its model.
        const notBoard = (p: string): boolean => !p.startsWith(top) || p === model || p.startsWith(`${model}/`);
        for (const c of textUnderButtons(measureScene(scene), buttons, notBoard)) {
            const key = `${c.text}\n${c.button}`;
            const seen = covered.get(key);
            if (seen) seen.pages.push(k); else covered.set(key, { ...c, text: inDeck(c.text), button: inDeck(c.button), pages: [k] });
        }
    };
    const { pages, moving } = deck
        ? await walkDeck(deck, roots, () => { board(steps.length); steps.push(captionStep(deck, frame)); return snapshot(); })
        : { pages: [snapshot()], moving: undefined };
    // A path from the frame down, so it reads short and resolves; from the scene's top when the frame is its root.
    const top = frame === scene.getSceneRoot() ? null : frame;
    const path = (o: SceneObject): string => {
        const names: string[] = [];
        for (let n: SceneObject | null = o; n && n !== top && n !== scene.getSceneRoot(); n = n.getParent()) names.unshift(n.name);
        return top ? `${top.name}/${names.join('/')}` : names.join('/');
    };
    const found = findFit({ pages, moving, path, only: page === undefined ? undefined : page - 1 });
    const newParts = deck ? ['', 'NEW PARTS BY PAGE, with the step its caption names, by shape and colour: diff each page with the parts you read for its step. '
        + 'A listed part no page adds was misread: zoom the steps again for it (arrow-less, under another part) before leaving it out.',
        ...newPartsText(pages, found.lastPage, steps, page === undefined ? undefined : page - 1)].join('\n') : '';
    const underButtons = [...covered.values()].filter(c => page === undefined || c.pages.includes(page - 1));
    const text = wordFit(found, { frame: frame.name, deck: deck?.name ?? null, pages: count, only: page, max, path, underButtons,
        budget: Math.max(3000, TOOL_RESULT_MAX_CHARS - 1000 - newParts.length) });
    return text + elsewhere(page === undefined ? null : findFit({ pages, moving, path }), page, [...covered.values()]) + newParts;
}

/** A button: a collider and a behavior graph that acts on select (makeButton's wiring); a hover-only collider is not one. */
const pressable = (o: SceneObject): boolean => !!o.getBehavior(BoxCollider)
    && (o.getBehavior(BehaviorGraph)?.getData().graph?.declarations ?? []).some(d => d.op === 'event/onSelect');

/** With one page asked for: what the other pages still show, so a fault there is not taken for fixed. */
function elsewhere<K>(all: FitFindings<K> | null, page: number | undefined, covered: readonly { pages: readonly number[] }[]): string {
    if (!all || page === undefined) return '';
    const off = (items: readonly { pages: readonly number[] }[]): number => items.filter(x => !x.pages.includes(page - 1)).length;
    const counts = ([['oversized', all.oversized], ['sunk', all.sunk], ['flicker', all.flicker], ['floating', all.floating], ['hover clash', all.clash], ['text under a button', covered]] as const)
        .map(([name, items]) => [name, off(items)] as const).filter(([, n]) => n > 0);
    return counts.length ? `\nOther pages also show ${counts.map(([name, n]) => `${n} ${name}`).join(', ')} not listed here: run check_overlaps without page to see them.` : '';
}

const mm = (v: number): string => (v * 1000).toFixed(1);
const AXES = 'XYZ';
const along = (m: Move): string => `${m.sign > 0 ? '+' : '-'}${AXES[m.axis]}${m.axis === 1 ? (m.sign > 0 ? ' (up)' : ' (down)') : ''} by ${m.mm.toFixed(1)} mm`;
const position = (m: Move): string => `[${m.position.map(v => +v.toFixed(5)).join(', ')}]`;
function range(pages: readonly number[]): string {
    const ks = pages.map(k => k + 1);
    if (ks.length === 1) return `page ${ks[0]}`;
    return `pages ${ks[0]}-${ks[ks.length - 1]}${ks.length === ks[ks.length - 1] - ks[0] + 1 ? '' : ` (${ks.length} of them)`}`;
}

/** `budget`: characters the findings may take, so a long category never hides the ones after it from a capped tool result. */
interface Wording<K> {
    frame: string; deck: string | null; pages: number; only?: number; max: number; path: (k: K) => string; budget: number;
    /** Board text a button covers, by object path, with the pages (0-based) it does so on. */
    underButtons: readonly (TextUnderButton & { pages: readonly number[] })[];
}

/** The findings as the tool's text: a header, then each category with its count and its own cap. */
function wordFit<K>(f: FitFindings<K>, w: Wording<K>): string {
    const q = (k: K): string => `"${w.path(k)}"`;
    const added = (k: K): string => (w.deck ? ` (added page ${f.firstPage.get(k)! + 1})` : '');
    const addedAll = (ks: K[]): string => {
        if (!w.deck) return '';
        const first = Math.min(...ks.map(k => f.firstPage.get(k)!)) + 1, last = Math.max(...ks.map(k => f.firstPage.get(k)!)) + 1;
        return first === last ? ` (added page ${first})` : ` (added pages ${first}-${last})`;
    };
    const at = (pages: readonly number[]): string => (w.deck ? `; at rest on ${range(pages)}` : '');
    const names = (ks: K[]): string => ks.slice(0, 3).map(q).join(', ') + (ks.length > 3 ? ` and ${ks.length - 3} more` : '');
    const set = (k: K, m: Move): string => `set_transform ${q(k)} position ${position(m)}, which moves it ${along(m)}`;
    /** One move for a group: a shared parent-local delta when every part's parent turns it alike, else each position. */
    const setAll = (ks: K[], ms: Move[]): string => {
        const same = ms.every(m => m.by.every((v, i) => Math.abs(v - ms[0].by[i]) <= 1e-9));
        if (same) return `set_transform with \`objects\` and delta_position [${ms[0].by.map(v => +v.toFixed(5)).join(', ')}] for each of them, which moves them ${along(ms[0])}`;
        const listed = ks.slice(0, 6).map((k, i) => `${q(k)} position ${position(ms[i])}`).join(', ');
        return `set_transform with \`objects\`: ${listed}${ks.length > 6 ? ` and the other ${ks.length - 6} by the same distance in the frame` : ''}, which moves them ${along(ms[0])}`;
    };
    const seatAll = (ks: K[], m: Move | null, ms: Move[] | null): string | null => (m ? set(ks[0], m) : ms ? setAll(ks, ms) : null);
    const lines: string[] = [];
    const scope = w.deck ? (w.only ? `page ${w.only} of "${w.deck}" at rest` : `all ${w.pages} pages of "${w.deck}", each at rest`) : 'as it stands';
    lines.push(`check_overlaps: "${w.frame}", ${scope}; ${f.parts} parts; model ${mm(f.size)} mm. Lengths are mm in "${w.frame}"'s frame; positions are each part's in its parent, for set_transform.`);
    lines.push(f.stud
        ? `Studs: ${mm(f.stud.height)} mm tall${f.stud.pitch ? `, ${mm(f.stud.pitch)} mm apart` : ''}, found on ${f.studded} parts; a seated stud is not an overlap, and others count when deeper than the larger of ${mm(f.stud.height * 0.9)} mm and 15% of the thinner part.`
        : 'An overlap counts when deeper than 15% of the thinner part.');
    const skipped = [
        f.skipped.cues ? `${f.skipped.cues} cue${f.skipped.cues === 1 ? '' : 's'} (shown only at rest on a page)` : '',
        f.skipped.hovering ? `${f.skipped.hovering} part${f.skipped.hovering === 1 ? '' : 's'} shown on one page only, hovering there` : '',
        f.skipped.open.length ? `${f.skipped.open.length} part${f.skipped.open.length === 1 ? '' : 's'} with no closed surface (${names(f.skipped.open)}), so nothing can be inside ${f.skipped.open.length === 1 ? 'it' : 'them'}` : '',
    ].filter(Boolean);
    if (skipped.length) lines.push(`Not checked: ${skipped.join('; ')}.`);
    const under = w.underButtons;
    lines.push(`Found: ${f.oversized.length ? `${f.oversized.length} oversized, ` : ''}${f.sunk.length} sunk, ${f.flicker.length} flicker, ${f.floating.length} floating, ${f.clash.length} hover clash${under.length ? `, ${under.length} text under a button` : ''}; by design (not counted): ${f.byDesign.length}.`);
    /** `rest`: the parts of each item, so a list cut short can still name what it left out. */
    const sections: { title: string; head: string[]; items: string[]; count: number; rest: K[][] }[] = [];
    const section = <T>(title: string, items: T[], word: (item: T) => string, head: string[] = [], parts: (item: T) => K[] = () => []): void => {
        if (items.length) sections.push({ title, head, items: items.slice(0, w.max).map((item, i) => `${i + 1}. ${word(item)}`), count: items.length, rest: items.map(parts) });
    };
    section('OVERSIZED', f.oversized, x => `${q(x.part)} is ${mm(x.across)} mm across, ${(x.across / x.rest).toFixed(0)}x the rest of the model (${mm(x.rest)} mm)${x.pages.length ? at(x.pages) : ''}: a scale or unit slip (cm or mm read as m, a size bound to the wrong value)? Give it its real size, unless it is a base, board or backdrop that big by design.`, [], x => [x.part]);
    const whole = f.sunk.filter(s => s.whole);
    section('SUNK', f.sunk, s => {
        const o = s.overlap;
        const fix = s.move ? ` Seat it: ${set(s.moved, s.move)}.` : ' No seat move: check it (above).';
        return `${q(s.moved)}${added(s.moved)} is sunk ${mm(o.depth)} mm${s.whole ? ' (its full thickness: placed by origin, not face)' : ''} along ${AXES[o.axis]} into ${q(s.into)}${added(s.into)}; overlap ${o.extent.map(mm).join(' x ')} mm${at(s.pages)}.${fix}${s.flush ? ' Their flush faces also flicker.' : ''}`;
    }, [
        ...(whole.length > 1 ? [`${whole.length} sit their full thickness deep (${names(whole.map(s => s.moved))}): each was placed by its origin where its face should meet, so its build's anchor or origin is off. Fix the placement rule they share, not each part.`] : []),
        ...(f.sunk.some(s => !s.move) ? ['With no seat move: check which part is misplaced or oversized; leave it if one is fitted into or round the other by design: threaded or clipped on, wrapped round, teeth meshing, a visor on a helmet.'] : []),
    ], s => [s.moved]);
    section('FLICKER', f.flicker, x => `${q(x.a)}${added(x.a)} and ${q(x.b)} have flush faces facing the same way (${(x.area * 1e6).toFixed(0)} mm2), so they flicker${at(x.pages)}.`
        + (x.move ? ` If ${q(x.a)} sits on or beside it: ${set(x.a, x.move)}; if a duplicate, delete one.` : ''), [], x => [x.a]);
    const support = (s: Floating<K>): string => {
        const it = s.parts.length === 1 ? 'it' : 'them';
        const sideFix = s.side && seatAll(s.parts, s.side.move, s.side.moves);
        const side = s.side ? ` Nearer on another side: ${q(s.side.part)}, ${mm(s.side.gap)} mm along ${s.side.sign > 0 ? '+' : '-'}${AXES[s.side.axis]}; if that holds ${it} (a screw, pin or clip in its hole)${sideFix ? `, ${sideFix}` : `, move ${it} there`}.` : '';
        const fix = seatAll(s.parts, s.move, s.moves);
        if (s.under === null) return side || ' Nothing is under it: it is beside the model, or the part that should carry it is missing.';
        if (s.through) return `${side} ${q(s.under)} passes through it (a rim in a tyre, a pin in a hole): if they should touch, make ${q(s.under)} fill its hole; don't seat it.`;
        return `${side} ${side ? 'Else, first' : 'First'} part under ${it}: ${q(s.under)}, ${mm(s.drop)} mm below${fix ? `; seat ${it}: ${fix}` : ''}.`;
    };
    section('FLOATING', f.floating, x => `${names(x.parts)}${addedAll(x.parts)} touch${x.parts.length === 1 ? 'es' : ''} nothing that holds ${x.parts.length === 1 ? 'it' : 'them'}${at(x.pages)}.${support(x)}`, [], x => x.parts);
    section('HOVER CLASH', f.clash, x => `hovering ${q(x.hovering)} passes ${mm(x.depth)} mm through ${q(x.other)} on ${range(x.pages)}: hover it along its fit so it clears, or flip the hover direction.`, [], x => [x.hovering]);
    const kinds = { embedded: 'one wholly inside the other (a rim in a tyre, a screw in its hole, a part in a tube, a mark or coat round a part)', insertion: 'a pin, axle, bolt or threaded end in a hole the model leaves out (or a hub)', surface: 'a line, bead or wire lying along the other' };
    section('TEXT UNDER A BUTTON', [...under], x => `"${x.text}" lies under the button "${x.button}" (${x.overlap.map(mm).join(' x ')} mm)${at(x.pages)}: `
        + 'the buttons own the board\'s last row; move the text above them, or below them inside the board.');
    section('BY DESIGN, NOT COUNTED', f.byDesign, x => `${q(x.a)} and ${q(x.b)}: ${kinds[x.kind]}${at(x.pages)}. Leave it unless it is a mistake.`);
    const byDesign = sections.find(x => x.title === 'BY DESIGN, NOT COUNTED');
    // A list cut short names the parts it left out (by path, as every item does), so no fault hides behind the cap.
    const leftOut = (x: typeof sections[number]): string => {
        const ks = [...new Set(x.rest.slice(x.items.length).flat())];
        if (!ks.length) return '';
        const shown = ks.slice(0, 12).map(q);
        return `: ${shown.join(', ')}${ks.length > shown.length ? ` and ${ks.length - shown.length} more parts` : ''}`;
    };
    const more = (x: typeof sections[number]): string => (x === byDesign
        ? `... and ${x.count - x.items.length} more by design: leave them unless one is a mistake.`
        : `... and ${x.count - x.items.length} more ${x.title.toLowerCase()}${leftOut(x)}. Fix these too, then run again.`);
    // Over budget: the by-design list gives way first, since nothing in it needs fixing; then drop the last item of the
    // longest list until it fits, so every category keeps its first items.
    const length = (x: typeof sections[number]): number => [...x.head, ...x.items].join('\n').length
        + (x.items.length < x.count ? more(x).length : 0);
    for (let total = sections.reduce((n, x) => n + length(x) + 120, 0); total > w.budget;) {
        const longest = byDesign?.items.length ? byDesign : sections.filter(x => x.items.length > 1).sort((a, b) => length(b) - length(a))[0];
        if (!longest) break;
        const before = length(longest);
        longest.items.pop();
        total -= before - length(longest);
    }
    for (const x of sections) {
        lines.push('', `${x.title} (${x.count})`, ...x.head, ...x.items);
        if (x.items.length < x.count) lines.push(more(x));
    }
    if (!f.oversized.length && !f.sunk.length && !f.flicker.length && !f.floating.length && !f.clash.length && !under.length) lines.push('', 'No faults.');
    lines.push('', 'Fix a part once even when it shows in several items, then run check_overlaps again; by-design overlaps stay counted.'
        + (w.deck ? ' Pages are 1-based, as in screenshot({page}). The editor was left on the page, states and picks it showed.' : ''));
    return lines.join('\n');
}

